import { CartLinesUpdateEvent } from '@shopify/events';

class RitualClassicPurchase extends HTMLElement {
  constructor(){
    super();
    this.variants=[];
    this.selected=[];
    this.imageOptionIndex=-1;
    this.deliveryTimer=null;
    this.weekdayNames=null;
    this.monthNames=null;
    this.sticky=null;
    this.stickyTriggerObserver=null;
    this.stickyFooterObserver=null;
    this.stickyPassed=false;
    this.stickyFooterVisible=false;
    this.offerSelections=[];
    this.activeOffer=1;
    this.offerSubmitting=false;
    this.giftsByOffer=new Map();
    this.giftSelections=new Map();
    this.giftZoomTrigger=null;
  }
  connectedCallback(){
    const source=this.querySelector('[data-rcp-variants]');
    if(!source)return;
    try{this.variants=JSON.parse(source.textContent||'[]')}catch(_){this.variants=[]}
    if(!this.variants.length)return;
    const initialId=Number(this.dataset.variantId||0);
    let initial=this.variants.find(v=>v.id===initialId)||this.variants.find(v=>v.available)||this.variants[0];
    this.selected=[...(initial?.options||[])];
    this.activeOffer=Math.max(1,Math.min(3,Number(this.dataset.defaultOffer||1)));
    if(!this.querySelector(`[data-rcp-offer-group="${this.activeOffer}"]`))this.activeOffer=Number(this.querySelector('[data-rcp-offer-group]')?.dataset.rcpOfferGroup||1);
    this.setupSizeSystems();
    initial=this.resolveInitialSizeVariant(initial)||initial;
    this.selected=[...(initial?.options||[])];
    this.offerSelections=[initial?.id,initial?.id,initial?.id];
    this.querySelectorAll('[data-rcp-offer-group], [data-rcp-standalone-gifts]').forEach(group=>{
      try{this.giftsByOffer.set(Number(group.dataset.rcpOfferGroup||1),JSON.parse(group.querySelector('[data-rcp-gifts-json]')?.textContent||'[]'))}
      catch(_){this.giftsByOffer.set(Number(group.dataset.rcpOfferGroup),[])}
    });
    this.parseDateLabels();
    this.renderStockCount();
    this.imageOptionIndex=this.resolveImageOption();
    this.decorateImageOption();
    this.addEventListener('click',this.onClick);
    this.addEventListener('change',this.onChange);
    this.addEventListener('submit',this.onOfferSubmit,true);
    this.addEventListener('shopify:cart:lines-update',this.onCartLinesUpdate);
    this.giftDialog=this.querySelector('[data-rcp-gift-dialog]');
    this.giftDialog?.addEventListener('close',this.onGiftDialogClose);
    // Preserve the theme's native gallery position on a normal PDP load.
    // Only an explicit ?variant=ID deep-link is allowed to select variant media
    // before the customer interacts with this picker.
    this.sync(initial,false);
    this.selectOffer(this.activeOffer);
    this.renderOfferGifts();
    this.syncExplicitUrlVariant(initial);
    this.renderDeliveryDate();
    this.setupSticky();
    this.purchaseScope=this.closest('.shopify-section')||this.parentElement;
    this.purchaseScope?.addEventListener('ritual:variant-change',this.onSiblingVariantChange);
    this.refreshPurchaseRoles();
  }
  disconnectedCallback(){
    this.purchaseScope?.removeEventListener('ritual:variant-change',this.onSiblingVariantChange);
    this.refreshPurchaseRoles();
    this.removeEventListener('click',this.onClick);
    this.removeEventListener('change',this.onChange);
    this.removeEventListener('submit',this.onOfferSubmit,true);
    this.removeEventListener('shopify:cart:lines-update',this.onCartLinesUpdate);
    this.giftDialog?.removeEventListener('close',this.onGiftDialogClose);
    this.stickyTriggerObserver?.disconnect();
    this.stickyFooterObserver?.disconnect();
    if(this.deliveryTimer){clearTimeout(this.deliveryTimer);this.deliveryTimer=null}
  }

  refreshPurchaseRoles(){
    if(!this.purchaseScope)return;
    const roots=[...this.purchaseScope.querySelectorAll('ritual-classic-purchase')].filter(root=>root.isConnected);
    roots.forEach(root=>{
      const hasBundle=roots.some(other=>other.dataset.productId===root.dataset.productId&&other.dataset.bundleOnly==='true');
      root.toggleAttribute('data-rcp-information-only',hasBundle&&root.dataset.bundleOnly!=='true');
    });
  }
  onSiblingVariantChange=(event)=>{
    if(event.target===this||!event.detail?.interactive||String(event.detail.productId)!==String(this.dataset.productId))return;
    const variant=this.variants.find(item=>Number(item.id)===Number(event.detail.variant?.id));
    if(!variant)return;
    for(const fieldset of this.querySelectorAll?.('[data-rcp-size-system][data-rcp-size-ready="true"]')||[]){
      const value=variant.options?.[Number(fieldset.dataset.optionIndex||0)]||'';
      const tab=[...fieldset.querySelectorAll('[data-rcp-size-tab]')].find(item=>this.sizePrefixMatches(value,item.dataset.sizePrefix||''));
      if(tab){fieldset.dataset.rcpSizeActive=String(tab.dataset.sizeGroup);this.paintSizeTabs(fieldset);}
    }
    this.selected=[...(variant.options||[])];
    this.sync(variant,false);
  };
  renderStockCount(){
    const node=this.querySelector('[data-rcp-stock-count]');
    if(!node||node.dataset.stockMode!=='session_random')return;
    const configuredMin=Math.max(1,Math.min(30,Number(node.dataset.stockMin)||3));
    const configuredMax=Math.max(1,Math.min(30,Number(node.dataset.stockMax)||7));
    const min=Math.min(configuredMin,configuredMax);
    const max=Math.max(configuredMin,configuredMax);
    const key=`ritual-stock:${this.dataset.productId||'product'}:${min}-${max}`;
    let count;
    try{count=Number(sessionStorage.getItem(key))}catch(_){}
    if(!Number.isInteger(count)||count<min||count>max){
      const random=new Uint32Array(1);
      if(globalThis.crypto?.getRandomValues)globalThis.crypto.getRandomValues(random);
      else random[0]=Math.floor(Math.random()*4294967296);
      count=min+random[0]%(max-min+1);
      try{sessionStorage.setItem(key,String(count))}catch(_){}
    }
    node.textContent=String(count);
  }

  setupSticky(){
    this.sticky=this.querySelector('[data-rcp-sticky]');
    if(!this.sticky)return;
    const trigger=this.querySelector('[data-rcp-submit]')||this.querySelector('.rcp');
    if(trigger){
      this.stickyTriggerObserver=new IntersectionObserver(([entry])=>{
        this.stickyPassed=!entry.isIntersecting&&entry.boundingClientRect.bottom<0;
        this.updateStickyVisibility();
      },{threshold:0});
      this.stickyTriggerObserver.observe(trigger);
    }
    const footerGroup=document.querySelector('.shopify-section-group-footer-group');
    const footer=footerGroup?.closest('footer')||footerGroup||document.querySelector('body > footer');
    if(footer){
      this.stickyFooterObserver=new IntersectionObserver(([entry])=>{
        this.stickyFooterVisible=entry.isIntersecting;
        this.updateStickyVisibility();
      },{threshold:0});
      this.stickyFooterObserver.observe(footer);
    }
  }
  updateStickyVisibility(){
    if(!this.sticky)return;
    const visible=this.stickyPassed&&!this.stickyFooterVisible;
    this.sticky.classList.toggle('is-visible',visible);
    this.sticky.setAttribute('aria-hidden',visible?'false':'true');
  }
  onStickyAction(){
    const target=this.querySelector('.rcp__picker')||this.querySelector('[data-rcp-submit]')||this.querySelector('.rcp')||this;
    const reduced=matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({behavior:reduced?'auto':'smooth',block:'center'});
    const focusTarget=target.querySelector?.('[data-rcp-value].is-selected:not(:disabled), [data-rcp-submit]');
    window.setTimeout(()=>focusTarget?.focus?.({preventScroll:true}),reduced?0:420);
  }

  onCartLinesUpdate=(event)=>{
    if(event.action!=='add'||!this.contains(event.target))return;
    const shouldCheckout=this.dataset.skipCart==='true';
    const shouldOpen=!shouldCheckout&&this.dataset.postAdd!=='stay';
    const autoOpenComponent=document.querySelector('cart-drawer-component[auto-open]');
    const suppressed=Boolean((shouldCheckout||!shouldOpen)&&autoOpenComponent);
    if(suppressed)autoOpenComponent.removeAttribute('auto-open');
    this.setBusy(true);

    const restore=()=>{
      if(suppressed&&autoOpenComponent?.isConnected)autoOpenComponent.setAttribute('auto-open','');
      this.setBusy(false);
    };
    const complete=(result)=>{
      if(result?.detail?.didError)return;
      if(shouldCheckout){window.location.assign(`${window.Shopify?.routes?.root||'/'}checkout`);return}
      if(shouldOpen)this.openCartDrawer();
    };

    if(event.promise?.then){
      event.promise.then(complete).catch(()=>{}).finally(restore);
    }else{
      queueMicrotask(()=>{if(shouldCheckout){window.location.assign(`${window.Shopify?.routes?.root||'/'}checkout`)}else if(shouldOpen)this.openCartDrawer();restore()});
    }
  }
  openCartDrawer(){
    requestAnimationFrame(()=>{
      const drawer=document.querySelector('theme-drawer#cart-drawer');
      if(typeof drawer?.open==='function'){drawer.open();return}
      const trigger=document.querySelector('[data-testid="cart-drawer-trigger"]');
      if(trigger)trigger.click();else window.location.assign(`${window.Shopify?.routes?.root||'/'}cart`);
    });
  }
  setBusy(busy){
    const button=this.querySelector('[data-rcp-submit]');
    if(!button)return;
    const label=button.querySelector('[data-rcp-submit-label]');
    if(busy){
      button.disabled=true;
      button.setAttribute('aria-busy','true');
      if(label)label.textContent=this.dataset.loadingText||this.dataset.addText||'';
      return;
    }
    button.removeAttribute('aria-busy');
    const variant=this.variants.find(v=>String(v.id)===String(this.dataset.variantId));
    button.disabled=!variant?.available;
    if(label)label.textContent=variant?.available?(this.dataset.addText||''):(this.dataset.soldText||'');
  }
  setupSizeSystems(){
    this.querySelectorAll('[data-rcp-size-system]').forEach(fieldset=>{
      const tabs=[...fieldset.querySelectorAll('[data-rcp-size-tab]')];
      const choices=[...fieldset.querySelectorAll('[data-rcp-value]')];
      if(!tabs.length||!choices.length)return;
      tabs.forEach(tab=>{
        const prefix=String(tab.dataset.sizePrefix||'').trim();
        const has=Boolean(prefix)&&choices.some(choice=>this.sizePrefixMatches(choice.dataset.value||'',prefix));
        tab.hidden=!has;
      });
      const usable=tabs.filter(tab=>!tab.hidden);
      if(!usable.length){
        fieldset.dataset.rcpSizeReady='false';
        tabs.forEach(tab=>tab.hidden=true);
        return;
      }
      fieldset.dataset.rcpSizeReady='true';
      choices.forEach(choice=>{
        const raw=choice.dataset.value||'';
        const group=usable.find(tab=>this.sizePrefixMatches(raw,tab.dataset.sizePrefix||''));
        choice.dataset.sizeGroup=group?.dataset.sizeGroup||'';
        const label=choice.querySelector('.rcp__value-label');
        if(label&&group)label.textContent=this.stripSizePrefix(raw,group.dataset.sizePrefix||'');
      });
      const target=this.defaultSizeTab(fieldset,usable);
      fieldset.dataset.rcpSizeActive=target?.dataset.sizeGroup||usable[0].dataset.sizeGroup||'';
      this.paintSizeTabs(fieldset);
    });
  }
  sizePrefixPattern(prefix){
    const compact=String(prefix||'').trim().toLocaleLowerCase().replace(/[\s_\-–—:./]+/g,'');
    if(!compact)return null;
    const escaped=[...compact].map(char=>char.replace(/[.*+?^${}()|[\]\\]/g,'\\$&')).join('[\\s_\\-–—:./]*');
    return new RegExp(`^${escaped}(?:[\\s_\\-–—:./]+|(?=\\d))`,'iu');
  }
  sizePrefixMatches(value,prefix){
    const pattern=this.sizePrefixPattern(prefix);
    return Boolean(pattern)&&pattern.test(String(value||'').trim());
  }
  stripSizePrefix(value,prefix){
    const raw=String(value||'').trim();
    const pattern=this.sizePrefixPattern(prefix);
    if(!pattern)return raw;
    const match=raw.match(pattern);
    return match?raw.slice(match[0].length).trim():raw;
  }
  selectedSizeTab(fieldset,tabs){
    const idx=Number(fieldset.dataset.optionIndex||0);
    const current=this.selected[idx]||'';
    return tabs.find(tab=>this.sizePrefixMatches(current,tab.dataset.sizePrefix||''))||null;
  }
  defaultSizeTab(fieldset,tabs){
    const forced=String(this.dataset.sizeDefault||'auto');
    if(forced!=='auto')return tabs.find(tab=>String(tab.dataset.sizeGroup)===forced)||tabs[0];
    const country=String(this.dataset.country||'').toUpperCase();
    const eu=new Set(['AT','BE','BG','HR','CY','CZ','DE','DK','EE','ES','FI','FR','GR','HU','IE','IT','LT','LU','LV','MT','NL','PL','PT','RO','SE','SI','SK','NO','CH','IS','LI','AE','SA','QA','KW','BH','OM']);
    let keys=[];
    if(country==='GB')keys=['uk','gb'];
    else if(country==='US'||country==='CA')keys=['us w','usw','women','us','us m','usm'];
    else if(country==='AU'||country==='NZ')keys=['uk','au'];
    else if(eu.has(country))keys=['eu','eur'];
    const normalized=tab=>`${tab.textContent||''} ${tab.dataset.sizePrefix||''}`.toLocaleLowerCase().replace(/[^a-z0-9]+/g,' ');
    for(const key of keys){
      const found=tabs.find(tab=>normalized(tab).includes(key.replace(/[^a-z0-9]+/g,' ')));
      if(found)return found;
    }
    return this.selectedSizeTab(fieldset,tabs)||tabs[0];
  }
  paintSizeTabs(fieldset){
    const active=String(fieldset.dataset.rcpSizeActive||'');
    fieldset.querySelectorAll('[data-rcp-size-tab]').forEach(tab=>{
      const selected=String(tab.dataset.sizeGroup)===active;
      tab.classList.toggle('is-selected',selected);
      tab.setAttribute('aria-selected',selected?'true':'false');
      tab.tabIndex=selected?0:-1;
    });
  }
  sizeChoiceAllowed(fieldset,choice){
    if(fieldset?.dataset.rcpSizeReady!=='true')return true;
    const active=String(fieldset.dataset.rcpSizeActive||'');
    return !choice.dataset.sizeGroup||String(choice.dataset.sizeGroup)===active;
  }
  displayOptionValue(fieldset,value){
    if(fieldset?.dataset.rcpSizeReady!=='true')return value||'';
    const tab=[...fieldset.querySelectorAll('[data-rcp-size-tab]')].find(item=>this.sizePrefixMatches(value,item.dataset.sizePrefix||''));
    return tab?this.stripSizePrefix(value,tab.dataset.sizePrefix||''):(value||'');
  }
  resolveInitialSizeVariant(initial){
    let current=initial;
    this.querySelectorAll('[data-rcp-size-system][data-rcp-size-ready="true"]').forEach(fieldset=>{
      const idx=Number(fieldset.dataset.optionIndex||0);
      const active=String(fieldset.dataset.rcpSizeActive||'');
      const choices=[...fieldset.querySelectorAll('[data-rcp-value]')].filter(choice=>String(choice.dataset.sizeGroup||'')===active);
      if(!choices.length)return;
      const currentValue=current?.options?.[idx]||'';
      if(choices.some(choice=>(choice.dataset.value||'')===currentValue))return;
      const allowed=new Set(choices.map(choice=>choice.dataset.value||''));
      const selected=current?.options||this.selected||[];
      const candidates=this.variants.filter(v=>allowed.has(v.options?.[idx]||''));
      candidates.sort((a,b)=>this.sizeVariantScore(b,selected,idx)-this.sizeVariantScore(a,selected,idx));
      current=candidates[0]||current;
    });
    return current;
  }
  sizeVariantScore(variant,selected,sizeIndex){
    return (variant?.available?1000:0)+(variant?.options||[]).reduce((score,value,index)=>score+(index!==sizeIndex&&value===selected?.[index]?1:0),0);
  }
  switchSizeSystem(tab){
    const fieldset=tab.closest('[data-rcp-size-system]');
    if(!fieldset||fieldset.dataset.rcpSizeReady!=='true'||tab.hidden)return;
    fieldset.dataset.rcpSizeActive=String(tab.dataset.sizeGroup||'');
    this.paintSizeTabs(fieldset);
    const idx=Number(fieldset.dataset.optionIndex||0);
    const choices=[...fieldset.querySelectorAll('[data-rcp-value]')];
    choices.forEach(choice=>choice.classList.toggle('is-size-hidden',!this.sizeChoiceAllowed(fieldset,choice)));
    const allowed=new Set(choices.filter(choice=>this.sizeChoiceAllowed(fieldset,choice)).map(choice=>choice.dataset.value||''));
    const candidates=this.variants.filter(v=>allowed.has(v.options?.[idx]||''));
    candidates.sort((a,b)=>this.sizeVariantScore(b,this.selected,idx)-this.sizeVariantScore(a,this.selected,idx));
    const variant=candidates[0];
    if(variant){this.selected=[...variant.options];this.sync(variant,true)}
  }

  resolveImageOption(){
    const names=(this.dataset.optionNames||'').split('|').map(name=>name.trim());
    const normalize=name=>String(name||'').normalize('NFD').replace(/[\u0300-\u036f]/g,'').toLowerCase().trim();
    const exact=normalize(this.dataset.imageOptionName);
    if(exact){const index=names.findIndex(name=>normalize(name)===exact);if(index>=0)return index}
    const mode=this.dataset.imageOption||'auto';
    if(mode==='none')return -1;
    if(/^\d+$/.test(mode))return Math.min(names.length-1,Math.max(-1,Number(mode)-1));
    const imageNames=new Set(['color','colour','colore','couleur','farbe','cor','kolor','kleur','farg','farve','culoare','цвет','色','颜色','顏色','색상','لون','renk','style','stile','estilo','stil','modello','model','pattern','motif','disegno','print','design']);
    const namedIndex=names.findIndex(name=>imageNames.has(normalize(name)));
    if(namedIndex>=0)return namedIndex;
    const sizeNames=new Set(['size','taglia','taille','grosse','große','talla','tamanho','maat','storlek','rozmiar','размер','サイズ','尺码','尺寸','사이즈','مقاس']);
    return names.length>0&&!sizeNames.has(normalize(names[0]))?0:-1;
  }
  representativeVariant(index,value){
    const previous=this.selected.slice(0,index);
    const matchesPrevious=v=>previous.every((selected,j)=>!selected||v.options?.[j]===selected);
    return this.variants.find(v=>v.available&&v.options?.[index]===value&&matchesPrevious(v)&&v.image)
      ||this.variants.find(v=>v.available&&v.options?.[index]===value&&v.image)
      ||this.variants.find(v=>v.options?.[index]===value&&v.image)
      ||null;
  }
  decorateImageOption(){
    this.querySelectorAll('[data-rcp-option]').forEach(fieldset=>{
      const idx=Number(fieldset.dataset.optionIndex);
      const isImage=idx===this.imageOptionIndex;
      fieldset.classList.toggle('is-image-option',isImage);
      if(!isImage)return;
      fieldset.querySelectorAll('[data-rcp-value]').forEach(button=>this.paintSwatch(button,idx));
    });
  }
  paintSwatch(button,index){
    const v=this.representativeVariant(index,button.dataset.value||'');
    const holder=button.querySelector('[data-rcp-value-image]');
    if(!holder)return;
    if(v?.image){
      const current=holder.querySelector('img');
      if(current?.src&&current.src.includes(v.image))return;
      const img=document.createElement('img');
      img.src=v.image;img.alt='';img.loading='lazy';img.decoding='async';
      holder.replaceChildren(img);
    }else holder.replaceChildren();
  }
  selectOffer(quantity){
    if(!Number.isInteger(quantity)||!this.querySelector(`[data-rcp-offer-group="${quantity}"]`))return;
    this.activeOffer=quantity;
    const input=this.querySelector('[data-rcp-qty]');
    if(input){input.value=String(this.activeOfferQuantity());input.dispatchEvent(new Event('change',{bubbles:true}))}
    this.querySelectorAll('[data-rcp-offer]').forEach(card=>{
      const selected=Number(card.dataset.rcpOffer)===quantity;
      card.classList.toggle('is-selected',selected);
      card.setAttribute('aria-pressed',String(selected));
      card.closest('[data-rcp-offer-group]')?.classList.toggle('is-selected',selected);
    });
    this.renderOfferItems();
    this.renderOfferGifts();
    this.updateOfferPrices();
  }
  variantForOfferSlot(index){
    const id=Number(this.offerSelections[index]||this.offerSelections[0]||this.dataset.variantId);
    const current=this.variants.find(v=>Number(v.id)===id)||this.variants.find(v=>v.available)||this.variants[0];
    const resolved=this.resolveInitialSizeVariant(current);
    if(resolved)this.offerSelections[index]=resolved.id;
    return resolved;
  }
  activeOfferQuantity(){
    const group=this.querySelector(`[data-rcp-offer-group="${this.activeOffer}"]`);
    return Math.max(1,Math.min(10,Number(group?.dataset.rcpOfferQuantity||this.activeOffer)));
  }
  configuredGifts(offer=this.activeOffer){
    const products=this.giftsByOffer.get(offer)||[];
    const group=this.querySelector(`[data-rcp-offer-group="${offer}"]`)||this.querySelector('[data-rcp-standalone-gifts]');
    return products.map((gift,index)=>{
      const product=gift.product||gift;
      const isVirtual=gift.kind==='virtual'||gift.kind==='service';
      const variants=isVirtual?[]:(product.variants||[]);
      const selectedId=Number(this.giftSelections.get(`${offer}:${index}`)||0);
      const variant=variants.find(item=>Number(item.id)===selectedId&&item.available)||variants.find(item=>item.available)||variants[0]||null;
      const mode=group?.dataset.rcpGiftMode||'free';
      const percent=group?.dataset.rcpGiftPercent;
      return {product,variant,index,isVirtual,virtualValue:gift.virtualValue||'',available:isVirtual?Boolean(product.title):Boolean(variant?.available),discountReady:isVirtual||Boolean(variant?.available),mode,percent:Math.max(0,Math.min(99,Number(percent||0))),bannerText:gift.bannerText||'',desktopText:gift.desktopText||''};
    });
  }
  selectedGifts(){
    return this.configuredGifts().filter(({available})=>available);
  }
  renderOfferGifts(){
    this.querySelectorAll('[data-rcp-offer-group], [data-rcp-standalone-gifts]').forEach(group=>{
      const holder=group.querySelector('[data-rcp-offer-gifts]');
      if(!holder)return;
      const offer=Number(group.dataset.rcpOfferGroup||1);
      const active=offer===this.activeOffer;
      const gifts=this.configuredGifts(offer);
      holder.hidden=!gifts.length;
      holder.classList.toggle('xp-offer-gifts--preview',false);
      holder.replaceChildren();
      gifts.forEach(({product,variant,index,isVirtual,virtualValue,available,discountReady,mode,percent,bannerText,desktopText})=>{
        const originalImage=variant?.featured_image?.src||product.featured_image||product.images?.[0];
        const imageUrl=typeof originalImage==='string'?originalImage:originalImage?.src||'';
        const row=document.createElement('div');row.className=`xp-offer-gifts__item${isVirtual?' xp-offer-gifts__item--virtual':''}`;row.dataset.rcpGiftIndex=String(index);row.classList.toggle('xp-offer-gifts__item--no-image',!imageUrl);
        const media=document.createElement('span');media.className='xp-offer-gifts__media';
        const image=document.createElement('span');image.className='xp-offer-gifts__image';
        if(imageUrl){
          const img=document.createElement('img');img.src=imageUrl;img.alt='';img.loading='lazy';image.append(img);
        }
        media.append(image);
        if(imageUrl&&(isVirtual?this.dataset.virtualGiftZoomEnabled==='true':this.dataset.giftZoomEnabled==='true')){
          const zoom=document.createElement('button');zoom.type='button';zoom.className='rcp__gift-zoom';zoom.dataset.rcpGiftZoom='';zoom.dataset.zoomDesktop=imageUrl;zoom.dataset.zoomMobile=imageUrl;zoom.dataset.zoomAlt=desktopText||product.title||'';zoom.setAttribute('aria-label',String(this.dataset.giftZoomLabel||'View image of {product}').replaceAll('{product}',product.title||''));
          zoom.innerHTML='<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5"/><path d="m15.5 15.5 5 5M10.5 7.5v6m-3-3h6"/></svg>';
          media.append(zoom);
        }
        const details=document.createElement('span');details.className='xp-offer-gifts__details';
        const copy=document.createElement('span');copy.className='xp-offer-gifts__title';
        copy.textContent=bannerText||desktopText||product.title||'';
        details.append(copy);row.append(media,details);
        const availableVariants=(product.variants||[]).filter(item=>item.available);
        if(!isVirtual&&active&&available&&availableVariants.length>1){
          const options=document.createElement('span');options.className='xp-offer-gifts__options';
          (variant.options||[]).forEach((selectedValue,optionIndex)=>{
            const values=[...new Set(availableVariants.map(item=>item.options?.[optionIndex]).filter(Boolean))];
            if(values.length<2)return;
            const label=document.createElement('label');label.className='xp-offer-gifts__option';
            const name=product.options?.[optionIndex];const labelText=document.createElement('span');labelText.textContent=typeof name==='string'?name:name?.name||`Option ${optionIndex+1}`;label.append(labelText);
            const select=document.createElement('select');select.dataset.rcpGiftSelect=String(index);select.dataset.rcpGiftOptionIndex=String(optionIndex);select.setAttribute('aria-label',`${product.title||'Gift'} ${labelText.textContent}`);
            values.forEach(value=>{const choice=document.createElement('option');choice.value=value;choice.textContent=value;choice.selected=value===selectedValue;select.append(choice)});
            label.append(select);options.append(label);
          });
          if(options.childElementCount)details.append(options);
        }
        const price=document.createElement('span');price.className='xp-offer-gifts__price';
        const reference=Math.max(Number(variant?.price||0),Number(variant?.compare_at_price||0));
        const shownPrice=isVirtual?0:(!discountReady?Number(variant?.price||0):mode==='discounted'?Math.round(Number(variant?.price||0)*(100-percent)/100):0);
        if(isVirtual&&String(virtualValue).trim()){const compare=document.createElement('del');compare.textContent=String(virtualValue).trim();price.append(compare)}
        else if(reference>shownPrice){const compare=document.createElement('del');compare.textContent=this.money(reference);price.append(compare)}
        const badge=document.createElement('strong');badge.textContent=!available?(this.dataset.giftUnavailableLabel||'Unavailable'):shownPrice>0?this.money(shownPrice):(this.dataset.freePriceLabel||'FREE');if(!discountReady)badge.classList.add('rcp__gift-warning');price.append(badge);row.append(price);
        holder.append(row);
      });
    });
  }
  renderOfferItems(){
    const names=String(this.dataset.optionNames||'').split('|');
    this.querySelectorAll('[data-rcp-offer-group]').forEach(group=>{
      const quantity=Math.max(1,Math.min(10,Number(group.dataset.rcpOfferQuantity||group.dataset.rcpOfferGroup||1)));
      const paid=Math.max(1,Number(group.dataset.rcpOfferPaid||quantity));
      const holder=group.querySelector('[data-rcp-offer-items]');
      if(!holder)return;
      const active=Number(group.dataset.rcpOfferGroup)===this.activeOffer;
      holder.hidden=!active;
      if(!active){holder.replaceChildren();return}
      holder.replaceChildren();
      for(let index=0;index<quantity;index++){
        const variant=this.variantForOfferSlot(index);
        const isFree=index>=paid;
        const row=document.createElement('div');row.className=`xp-bundle-item${isFree?' xp-bundle-item--free':''}`;row.dataset.rcpOfferItem=String(index);
        const marker=document.createElement('span');marker.className='xp-bundle-item__marker';marker.setAttribute('aria-hidden','true');row.append(marker);
        const media=document.createElement('div');media.className='xp-bundle-item__media';
        const image=document.createElement('div');image.className='xp-bundle-item__image';
        if(variant?.image){const img=document.createElement('img');img.src=variant.image;img.alt='';img.loading='lazy';image.append(img)}
        media.append(image);row.append(media);
        const fields=document.createElement('div');fields.className='xp-bundle-item__fields';
        if(this.dataset.defaultOnly!=='true'){
          names.forEach((name,optionIndex)=>{
            const fieldset=this.querySelector(`[data-rcp-option][data-option-index="${optionIndex}"]`);
            let values=[...new Set(this.variants.map(v=>v.options?.[optionIndex]).filter(Boolean))];
            if(fieldset?.dataset.rcpSizeReady==='true'){
              const allowed=new Set([...fieldset.querySelectorAll('[data-rcp-value]')].filter(choice=>this.sizeChoiceAllowed(fieldset,choice)).map(choice=>choice.dataset.value));
              values=values.filter(value=>allowed.has(value));
            }
            if(values.length<2)return;
            const label=document.createElement('label');label.className='xp-bundle-item__field';
            const span=document.createElement('span');span.textContent=name;label.append(span);
            const select=document.createElement('select');select.dataset.rcpOfferSelect=String(index);select.dataset.optionIndex=String(optionIndex);select.setAttribute('aria-label',`${name} ${index+1}`);
            values.forEach(value=>{const choice=document.createElement('option');choice.value=value;choice.textContent=this.displayOptionValue(fieldset,value);choice.selected=value===variant?.options?.[optionIndex];choice.disabled=!this.variants.some(v=>v.available&&v.options?.[optionIndex]===value);select.append(choice)});
            label.append(select);fields.append(label);
          });
        }
        if(!fields.childElementCount){const name=document.createElement('span');name.className='xp-bundle-item__name';name.textContent=this.dataset.productTitle||'';fields.append(name)}
        row.append(fields);
        const price=document.createElement('span');price.className=`xp-bundle-item__gift-price${isFree?'':' xp-bundle-item__gift-price--empty'}`;
        if(isFree){
          const reference=Math.max(Number(variant?.price||0),Number(variant?.compareAt||0));
          if(reference>0){const compare=document.createElement('del');compare.textContent=this.money(reference);price.append(compare)}
          const free=document.createElement('strong');free.textContent=this.dataset.freePriceLabel||'FREE';price.append(free);
        }else price.setAttribute('aria-hidden','true');
        row.append(price);holder.append(row);
      }
      requestAnimationFrame(()=>this.fitOfferSelects(holder));
    });
  }
  fitOfferSelects(container){
    if(!container?.isConnected)return;
    const selects=[...container.querySelectorAll('.xp-bundle-item__field select')];
    if(!selects.length)return;
    const context=document.createElement('canvas').getContext('2d');
    if(!context)return;
    selects.forEach(select=>{
      select.style.fontSize='';
      const field=select.closest('.xp-bundle-item__field');
      field?.classList.remove('xp-bundle-item__field--long');
      field?.querySelector('.xp-bundle-item__full-value')?.remove();
      field?.closest('.xp-bundle-item__fields')?.classList.remove('xp-bundle-item__fields--wrap');
    });
    const fit=select=>{
      const style=getComputedStyle(select);
      const base=parseFloat(style.fontSize)||12;
      const available=select.clientWidth-(parseFloat(style.paddingLeft)||0)-(parseFloat(style.paddingRight)||0)-6;
      const value=select.selectedOptions?.[0]?.textContent?.trim()||'';
      if(!value||available<=0)return false;
      const widthAt=size=>{context.font=`${style.fontStyle||'normal'} ${style.fontWeight||'700'} ${size}px ${style.fontFamily||'Arial,sans-serif'}`;return context.measureText(value).width};
      let size=base;
      while(size>8.5&&widthAt(size)>available)size=Math.max(8.5,size-.5);
      if(size<base)select.style.fontSize=`${size}px`;
      return widthAt(size)>available;
    };
    selects.forEach(select=>{
      if(!fit(select))return;
      const field=select.closest('.xp-bundle-item__field');
      field?.classList.add('xp-bundle-item__field--long');
      field?.closest('.xp-bundle-item__fields')?.classList.add('xp-bundle-item__fields--wrap');
    });
    selects.forEach(select=>{
      select.style.fontSize='';
      if(!fit(select))return;
      const field=select.closest('.xp-bundle-item__field');
      if(!field)return;
      const fullValue=document.createElement('span');fullValue.className='xp-bundle-item__full-value';fullValue.textContent=select.selectedOptions?.[0]?.textContent?.trim()||'';field.append(fullValue);
    });
  }
  parseOfferAmount(value){
    const raw=String(value??'').trim();
    if(!/^\d+(?:[.,]\d{1,2})?$/.test(raw))return null;
    const cents=Math.round(Number(raw.replace(',','.'))*100);
    return Number.isSafeInteger(cents)?cents:null;
  }
  virtualGiftReference(value){
    const currency=window.Shopify?.currency?.active||this.dataset.shopCurrency||'USD';
    let raw=String(value||'').trim();
    let explicit=false;
    try{
      const symbol=new Intl.NumberFormat('en',{style:'currency',currency}).formatToParts(0).find(part=>part.type==='currency')?.value;
      for(const marker of [currency,symbol].filter(Boolean)){
        if(raw.includes(marker)){raw=raw.replace(marker,'').trim();explicit=true;}
      }
    }catch(_){}
    if(!explicit&&this.dataset.shopCurrency&&currency!==this.dataset.shopCurrency)return 0;
    return this.parseOfferAmount(raw)||0;
  }
  updateOfferPrices(){
    this.querySelectorAll('[data-rcp-offer]').forEach(card=>{
      const group=card.closest('[data-rcp-offer-group]');
      const quantity=Math.max(1,Math.min(10,Number(group?.dataset.rcpOfferQuantity||card.dataset.rcpOffer||1)));
      const paid=Math.max(1,Math.min(quantity,Number(group?.dataset.rcpOfferPaid||quantity)));
      const variants=Array.from({length:quantity},(_,index)=>this.variantForOfferSlot(index));
      const subtotal=variants.slice(0,paid).reduce((sum,variant)=>sum+Number(variant?.price||0),0);
      let compare=variants.reduce((sum,variant)=>sum+Math.max(Number(variant?.compareAt||0),Number(variant?.price||0)),0);
      const mode=group?.dataset.rcpDiscountMode||'none',value=Math.max(0,Number(group?.dataset.rcpDiscountValue||0));
      let price=mode==='percentage'?Math.round(subtotal*Math.max(0,100-value)/100):mode==='fixed'?Math.max(0,subtotal-Math.round(value*100)):subtotal;
      const gifts=this.configuredGifts(Number(group?.dataset.rcpOfferGroup||card.dataset.rcpOffer||1));
      gifts.forEach(gift=>{
        if(gift.isVirtual){compare+=this.virtualGiftReference(gift.virtualValue);return;}
        compare+=Math.max(Number(gift.variant?.compare_at_price||0),Number(gift.variant?.price||0));
        const charge=Number(gift.variant?.price||0);
        price+=gift.mode==='free'?0:gift.mode==='discounted'?Math.round(charge*(100-gift.percent)/100):charge;
      });
      const currency=window.Shopify?.currency?.active||this.dataset.shopCurrency||'USD';
      if(!this.dataset.shopCurrency||currency===this.dataset.shopCurrency){
        const manualPrice=this.parseOfferAmount(group?.dataset.rcpManualPrice);
        const manualCompare=this.parseOfferAmount(group?.dataset.rcpManualCompare);
        if(manualPrice!==null)price=manualPrice;
        if(manualCompare!==null)compare=manualCompare;
      }
      const thumbnail=card.querySelector('.rcp__offer-thumbnail img');
      if(thumbnail&&variants[0]?.image)thumbnail.src=variants[0].image;
      const priceNode=card.querySelector('[data-rcp-offer-price]');
      const compareNode=card.querySelector('[data-rcp-offer-compare]');
      const savingsNode=card.querySelector('[data-rcp-offer-savings]');
      if(priceNode)priceNode.textContent=this.money(price);
      if(compareNode){compareNode.hidden=compare<=price;compareNode.textContent=compare>price?this.money(compare):''}
      if(savingsNode){savingsNode.hidden=compare<=price;savingsNode.textContent=compare>price?String(savingsNode.dataset.template||'You save {amount}').replaceAll('{amount}',this.money(compare-price)):''}
    });
  }
  onChange=(event)=>{
    const giftSelect=event.target.closest('[data-rcp-gift-select]');
    if(giftSelect&&this.contains(giftSelect)){
      const index=Number(giftSelect.dataset.rcpGiftSelect);
      const optionIndex=Number(giftSelect.dataset.rcpGiftOptionIndex);
      const gift=this.giftsByOffer.get(this.activeOffer)?.[index];
      const product=gift?.product||gift;
      const available=(product?.variants||[]).filter(item=>item.available);
      const current=this.configuredGifts().find(item=>item.index===index)?.variant||available[0];
      if(!current)return;
      const wanted=[...(current?.options||[])];wanted[optionIndex]=giftSelect.value;
      const chosen=available.find(item=>item.options?.every((value,i)=>value===wanted[i]))||available.find(item=>item.options?.[optionIndex]===giftSelect.value);
      if(chosen)this.giftSelections.set(`${this.activeOffer}:${index}`,Number(chosen.id));
      this.renderOfferGifts();
      this.updateOfferPrices();
      return;
    }
    const select=event.target.closest('[data-rcp-offer-select]');
    if(!select||!this.contains(select))return;
    const slot=Number(select.dataset.rcpOfferSelect),optionIndex=Number(select.dataset.optionIndex);
    const current=this.variantForOfferSlot(slot);
    const wanted=[...(current?.options||[])];wanted[optionIndex]=select.value;
    const available=this.variants.filter(v=>v.available&&v.options?.[optionIndex]===select.value);
    const next=available.find(v=>v.options.every((value,index)=>value===wanted[index]))||available.sort((a,b)=>this.score(b,wanted,optionIndex)-this.score(a,wanted,optionIndex))[0];
    if(!next){this.renderOfferItems();return}
    this.offerSelections[slot]=next.id;
    if(slot===0)this.sync(next,true);
    else {this.renderOfferItems();this.updateOfferPrices()}
  };
  onOfferSubmit=(event)=>{
    if(!event.target.matches('form.rcp__native-form'))return;
    event.preventDefault();event.stopImmediatePropagation();
    this.addOfferToCart();
  };
  async addOfferToCart(){
    if(this.offerSubmitting)return;
    const hasOffers=Boolean(this.querySelector('[data-rcp-offer-group]'));
    const quantity=hasOffers?this.activeOfferQuantity():Math.max(1,Math.min(10,Math.floor(Number(this.querySelector('[data-rcp-qty]')?.value||1))));
    const selected=Array.from({length:quantity},(_,index)=>this.variantForOfferSlot(hasOffers?index:0));
    if(selected.some(variant=>!variant?.id||!variant.available)){
      const error=this.querySelector('.rcp__error');if(error){error.textContent=this.dataset.offerSelectionError||'Choose an available option for every piece.';error.classList.remove('hidden','is-notice')}
      return;
    }
    const offerGroup=this.querySelector(`[data-rcp-offer-group="${this.activeOffer}"]`)||this.querySelector('[data-rcp-standalone-gifts]');
    const discountCode=String(offerGroup?.dataset.rcpDiscountCode||'').trim();
    const configuredGifts=hasOffers||this.querySelector('[data-rcp-standalone-gifts]')?this.configuredGifts():[];
    const physicalGifts=configuredGifts.filter(gift=>!gift.isVirtual);
    const invalidGift=physicalGifts.find(gift=>!gift.available||!Number(gift.variant?.id));
    if(invalidGift){
      const box=this.querySelector('.rcp__error');
      if(box){box.textContent=this.dataset.giftUnavailableError||'A selected gift is unavailable.';box.classList.remove('hidden','is-notice')}
      return;
    }
    const gifts=physicalGifts;
    // One submit owns the entire add. Keep each chosen piece as one cart line,
    // as the working NPages purchase flow does, so the selected count is exact.
    const groupId=`ritual-${Date.now()}`;
    const paid=hasOffers?Math.max(1,Number(this.querySelector(`[data-rcp-offer-group="${this.activeOffer}"]`)?.dataset.rcpOfferPaid||quantity)):quantity;
    const items=selected.map((variant,index)=>({id:Number(variant.id),quantity:1,properties:{_Ritual_group:groupId,_Ritual_slot:String(index+1),_Ritual_offer:hasOffers?String(this.activeOffer):'single',_Ritual_free:index>=paid?'true':'false'}}));
    gifts.forEach(({variant,index,mode,percent})=>items.push({id:Number(variant.id),quantity:1,properties:{_Ritual_group:groupId,_Ritual_slot:`gift-${index+1}`,_Ritual_offer:String(this.activeOffer),_Ritual_attached_gift:'true',_Ritual_gift_mode:mode,_Ritual_gift_max:mode==='discounted'?String(Math.round(Number(variant.price||0)*(100-percent)/100)):'0'}}));
    const sectionIds=[...document.querySelectorAll('cart-items-component[data-section-id]')].map(item=>item.dataset.sectionId).filter(Boolean);
    const deferred=CartLinesUpdateEvent.createPromise();
    this.offerSubmitting=true;this.setBusy(true);
    this.dispatchEvent(new CartLinesUpdateEvent({action:'add',context:'product',lines:items.map(item=>({merchandiseId:String(item.id),quantity:item.quantity})),promise:deferred.promise}));
    try{
      const root=window.Shopify?.routes?.root||'/';
      const response=await fetch(window.Theme?.routes?.cart_add_url||`${root}cart/add.js`,{method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({items,sections:sectionIds.join(',')})});
      const result=await response.json();
      if(!response.ok||result.status)throw new Error(result.description||result.message||'Could not add your selection.');
      let discountError=false;
      let refreshedSections=result.sections;
      if(discountCode){
        try{
          const discountResponse=await fetch(`${root}cart/update.js`,{method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({discount:discountCode,sections:sectionIds})});
          if(!discountResponse.ok)discountError=true;
          else {const updated=await discountResponse.json();if(updated.sections)refreshedSections=updated.sections}
        }catch(_){discountError=true}
      }
      const cartResponse=await fetch(`${root}cart.js`,{headers:{Accept:'application/json'}});
      if(!cartResponse.ok)throw new Error('Could not refresh the cart.');
      let cart=await cartResponse.json();
      let giftPricingError=false;
      for(const gift of gifts){
        if(Number(gift.variant.price||0)===0)continue;
        const slot=`gift-${gift.index+1}`;
        const line=cart.items?.find(item=>item.properties?._Ritual_group===groupId&&item.properties?._Ritual_slot===slot);
        const expectedMax=gift.mode==='discounted'?Math.round(Number(gift.variant.price)*(100-gift.percent)/100):0;
        const actualPrice=Number(line?.final_price??line?.discounted_price??line?.price);
        if(!line||!Number.isFinite(actualPrice)||actualPrice>expectedMax){
          giftPricingError=true;
          const giftRow=offerGroup?.querySelector?.(`[data-rcp-gift-index="${gift.index}"]`);
          const badge=giftRow?.querySelector('.xp-offer-gifts__price strong');
          if(badge&&Number.isFinite(actualPrice)){
            badge.textContent=this.money(actualPrice);
            badge.classList.add('rcp__gift-warning');
            if(Number(gift.variant.price||0)<=actualPrice)giftRow.querySelector('.xp-offer-gifts__price del')?.remove();
          }
        }
      }
      deferred.resolve({cart:CartLinesUpdateEvent.createCartFromAjaxResponse(cart),detail:{items:cart.items,source:'ritual-classic-purchase',itemCount:items.length,productId:this.dataset.productId,sections:refreshedSections,didError:false}});
      const errorBox=this.querySelector('.rcp__error');
      if(errorBox){const message=giftPricingError?(this.dataset.giftDiscountNotApplied||'Gifts added. Check their final prices in the cart before checkout.'):discountError?(this.dataset.offerDiscountApplyError||'The offer was added, but its Shopify discount code could not be applied.'):'';errorBox.classList.toggle('hidden',!message);errorBox.classList.toggle('is-notice',giftPricingError&&!discountError);if(message)errorBox.textContent=message}
    }catch(error){
      deferred.reject(error);
      const box=this.querySelector('.rcp__error');if(box){box.textContent=error.message||this.dataset.offerSelectionError;box.classList.remove('hidden','is-notice')}
    }finally{this.offerSubmitting=false;this.setBusy(false)}
  }
  changeQuantity(delta){
    const input=this.querySelector('[data-rcp-qty]');
    if(!input)return;
    const min=Math.max(1,Number(input.min||1));
    const max=input.max?Number(input.max):Infinity;
    const current=Math.max(min,Number(input.value||min));
    input.value=String(Math.min(max,Math.max(min,current+delta)));
    input.dispatchEvent(new Event('change',{bubbles:true}));
  }
  openGuide(){
    const dialog=this.querySelector('[data-rcp-guide-dialog]');
    if(!dialog)return;
    if(typeof dialog.showModal==='function')dialog.showModal();else dialog.setAttribute('open','');
  }
  closeGuide(){
    const dialog=this.querySelector('[data-rcp-guide-dialog]');
    if(!dialog)return;
    if(typeof dialog.close==='function')dialog.close();else dialog.removeAttribute('open');
  }
  openGiftZoom(trigger){
    const dialog=this.querySelector('[data-rcp-gift-dialog]');
    const image=dialog?.querySelector('[data-rcp-gift-dialog-image]');
    if(!dialog||!image)return;
    const mobile=window.matchMedia?.('(max-width: 749px)').matches===true;
    image.src=mobile?trigger.dataset.zoomMobile:trigger.dataset.zoomDesktop;
    image.alt=trigger.dataset.zoomAlt||'';
    this.giftZoomTrigger=trigger;
    if(typeof dialog.showModal==='function')dialog.showModal();else dialog.setAttribute('open','');
  }
  closeGiftZoom(){
    const dialog=this.querySelector('[data-rcp-gift-dialog]');
    if(!dialog)return;
    if(typeof dialog.close==='function')dialog.close();else dialog.removeAttribute('open');
    this.giftZoomTrigger?.focus?.();
  }
  onGiftDialogClose=()=>this.giftZoomTrigger?.focus?.();
  onClick=(event)=>{
    const giftZoom=event.target.closest('[data-rcp-gift-zoom]');
    if(giftZoom&&this.contains(giftZoom)){this.openGiftZoom(giftZoom);return}
    const giftPreview=event.target.closest('[data-rcp-gift-preview]');
    if(giftPreview&&this.contains(giftPreview)){this.selectOffer(Number(giftPreview.dataset.rcpGiftPreview));return}
    const giftZoomClose=event.target.closest('[data-rcp-gift-zoom-close]');
    if(giftZoomClose&&this.contains(giftZoomClose)){this.closeGiftZoom();return}
    if(event.target.matches?.('[data-rcp-gift-dialog]')){this.closeGiftZoom();return}
    const guideOpen=event.target.closest('[data-rcp-guide]');
    if(guideOpen&&this.contains(guideOpen)){this.openGuide();return}
    const guideClose=event.target.closest('[data-rcp-guide-close]');
    if(guideClose&&this.contains(guideClose)){this.closeGuide();return}
    const offer=event.target.closest('[data-rcp-offer]');
    if(offer&&this.contains(offer)){this.selectOffer(Number(offer.dataset.rcpOffer));return}
    const qtyMinus=event.target.closest('[data-rcp-qty-minus]');
    if(qtyMinus&&this.contains(qtyMinus)){this.changeQuantity(-1);return}
    const qtyPlus=event.target.closest('[data-rcp-qty-plus]');
    if(qtyPlus&&this.contains(qtyPlus)){this.changeQuantity(1);return}
    const stickyAction=event.target.closest('[data-rcp-sticky-action]');
    if(stickyAction&&this.contains(stickyAction)){this.onStickyAction();return}
    const sizeTab=event.target.closest('[data-rcp-size-tab]');
    if(sizeTab&&this.contains(sizeTab)){this.switchSizeSystem(sizeTab);return}
    const button=event.target.closest('[data-rcp-value]');
    if(!button||button.disabled||!this.contains(button))return;
    const index=Number(button.dataset.optionIndex);
    const value=button.dataset.value||'';
    const proposal=[...this.selected];
    proposal[index]=value;
    const previous=proposal.slice(0,index);
    const candidates=this.variants.filter(v=>v.available&&v.options?.[index]===value&&previous.every((selected,j)=>!selected||v.options?.[j]===selected));
    if(!candidates.length)return;
    candidates.sort((a,b)=>this.score(b,proposal,index)-this.score(a,proposal,index));
    const variant=candidates[0];
    if(!variant)return;
    this.selected=[...variant.options];
    this.sync(variant,true);
  }
  exact(v,values){return values.every((value,index)=>v.options?.[index]===value)}
  score(v,values,clicked){
    let s=0;
    values.forEach((value,index)=>{if(index!==clicked&&value&&v.options?.[index]===value)s++});
    return s;
  }
  optionState(index,value){
    const previous=this.selected.slice(0,index);
    const matchesPrefix=v=>previous.every((selected,j)=>!selected||v.options?.[j]===selected)&&v.options?.[index]===value;
    return {
      exists:this.variants.some(matchesPrefix),
      available:this.variants.some(v=>v.available&&matchesPrefix(v))
    };
  }
  sync(variant,interactive){
    if(!variant)return;
    this.dataset.variantId=String(variant.id);
    this.selected=[...variant.options];
    this.querySelectorAll('[data-rcp-option]').forEach(fieldset=>{
      const idx=Number(fieldset.dataset.optionIndex);
      const selectedValue=this.selected[idx]||'';
      const selectedLabel=fieldset.querySelector('[data-rcp-selected-value]');
      if(selectedLabel)selectedLabel.textContent=this.displayOptionValue(fieldset,selectedValue);
      fieldset.querySelectorAll('[data-rcp-value]').forEach(button=>{
        const value=button.dataset.value||'';
        const state=this.optionState(idx,value);
        const chosen=value===selectedValue;
        button.classList.toggle('is-selected',chosen);
        button.classList.toggle('is-nonexistent',!state.exists);
        button.classList.toggle('is-size-hidden',!this.sizeChoiceAllowed(fieldset,button));
        button.classList.toggle('is-sold-out',state.exists&&!state.available);
        button.setAttribute('aria-pressed',chosen?'true':'false');
        button.disabled=!state.available&&!chosen;
        button.setAttribute('aria-disabled',button.disabled?'true':'false');
        if(idx===this.imageOptionIndex)this.paintSwatch(button,idx);
      });
    });
    const current=this.querySelector('[data-rcp-current-price]');
    const compare=this.querySelector('[data-rcp-compare-price]');
    if(current)current.textContent=variant.priceFormatted||'';
    if(compare){
      const show=Number(variant.compareAt||0)>Number(variant.price||0)&&variant.compareFormatted;
      compare.textContent=show?variant.compareFormatted:'';
      compare.hidden=!show;
    }
    this.updateDiscountBadge(variant);
    if(this.offerSelections.length)this.offerSelections[0]=variant.id;
    this.renderOfferItems();
    this.updateOfferPrices();
    const input=this.querySelector('input[name="id"]');if(input)input.value=String(variant.id);
    const button=this.querySelector('[data-rcp-submit]');
    if(button){
      button.disabled=!variant.available;
      const label=button.querySelector('[data-rcp-submit-label]');
      if(label)label.textContent=variant.available?(this.dataset.addText||''):(this.dataset.soldText||'');
    }
    const addContainer=this.querySelector('add-to-cart-component');
    if(addContainer&&variant.image)addContainer.dataset.productVariantMedia=variant.image;
    this.dispatchEvent(new CustomEvent('ritual:variant-change',{bubbles:true,detail:{productId:this.dataset.productId,variant,interactive:Boolean(interactive)}}));
    if(interactive){this.syncUrl(variant);this.syncGallery(variant)}
  }
  syncUrl(variant){
    try{const url=new URL(window.location.href);url.searchParams.set('variant',variant.id);history.replaceState({},'',url.toString())}catch(_){}
  }
  syncExplicitUrlVariant(variant){
    // Do not jump from editorial/universal product media to the first available
    // variant during a normal page load. Deep links are the only init-time
    // exception because the visitor explicitly requested that variant.
    if(!variant?.mediaId)return;
    let explicitId=0;
    try{explicitId=Number(new URL(window.location.href).searchParams.get('variant')||0)}catch(_){}
    if(!explicitId||explicitId!==Number(variant.id))return;
    requestAnimationFrame(()=>this.syncGallery(variant,{animate:false}));
  }
  syncGallery(variant,{animate=true}={}){
    if(!variant?.mediaId)return;
    const scope=this.closest('.shopify-section')||document;
    const gallery=scope.querySelector('media-gallery')||document.querySelector('media-gallery');
    if(!gallery)return;
    const selectMedia=()=>{
      const slides=[...(gallery.querySelectorAll('slideshow-slide')||[])];
      const idx=slides.findIndex(slide=>slide.querySelector(`[data-media-id="${variant.mediaId}"]`));
      if(idx>-1&&gallery.slideshow?.select){
        gallery.slideshow.select(idx,undefined,{animate});
        gallery.preloadImage?.(idx);
        return true;
      }
      const media=gallery.querySelector(`[data-media-id="${variant.mediaId}"]`);
      if(media){media.scrollIntoView?.({behavior:'smooth',block:'nearest',inline:'nearest'});return true}
      return false;
    };
    if(!selectMedia())requestAnimationFrame(selectMedia);
  }
  parseDateLabels(){
    const source=this.querySelector('[data-rcp-date-labels]');
    if(!source)return;
    try{
      const data=JSON.parse(source.textContent||'{}');
      this.weekdayNames=this.parseDateNames(data.weekdays,7);
      this.monthNames=this.parseDateNames(data.months,12);
    }catch(_){this.weekdayNames=null;this.monthNames=null}
  }
  parseDateNames(value,count){
    const raw=String(value||'').trim();
    if(!raw)return null;
    // Comma-separated lists are the canonical merchant/Translate & Adapt format.
    // Newlines remain accepted only as a backwards-compatible migration path.
    const parts=(raw.includes(',')?raw.split(','):raw.split(/\n+/)).map(v=>v.trim()).filter(Boolean);
    return parts.length===count?parts:null;
  }
  formatDeliveryDate(date){
    const locale=document.documentElement.lang||this.dataset.locale||navigator.language||'en';
    try{
      const formatter=new Intl.DateTimeFormat(locale,{weekday:'short',month:'long',day:'numeric'});
      const parts=formatter.formatToParts(date).map(part=>{
        if(part.type==='weekday'&&this.weekdayNames)return {...part,value:this.weekdayNames[date.getDay()]};
        if(part.type==='month'&&this.monthNames)return {...part,value:this.monthNames[date.getMonth()]};
        return part;
      });
      return parts.map(part=>part.value).join('');
    }catch(_){return date.toLocaleDateString(locale)}
  }
  money(cents){
    const locale=document.documentElement.lang||this.dataset.locale||navigator.language||'en';
    const currency=window.Shopify?.currency?.active||'USD';
    try{return new Intl.NumberFormat(locale,{style:'currency',currency}).format(Number(cents||0)/100)}catch(_){return String(cents||0)}
  }
  updateDiscountBadge(variant){
    const badge=this.querySelector('[data-rcp-discount-badge]');
    if(!badge)return;
    const mode=this.dataset.discountBadgeMode||'percentage';
    const compare=Number(variant?.compareAt||0),price=Number(variant?.price||0);
    const discount=Math.max(0,compare-price);
    const prefix=String(this.dataset.discountBadgePrefix||'').trim();
    let text='';
    if(mode==='custom'&&discount>0)text=String(this.dataset.discountBadgeCustom||'').trim();
    else if(discount>0&&compare>0){
      const value=mode==='amount'?this.money(discount):`${Math.round((discount/compare)*100)}%`;
      text=[prefix,value].filter(Boolean).join(' ');
    }
    badge.textContent=text;
    badge.hidden=!text;
  }
  scheduleDeliveryRefresh(){
    if(this.deliveryTimer)clearTimeout(this.deliveryTimer);
    const now=new Date();
    const next=new Date(now.getFullYear(),now.getMonth(),now.getDate()+1,0,0,2,0);
    const delay=Math.max(60000,next.getTime()-now.getTime());
    this.deliveryTimer=setTimeout(()=>this.renderDeliveryDate(),delay);
  }
  renderDeliveryDate(){
    const target=this.querySelector('[data-rcp-delivery-date]');if(!target)return;
    const date=new Date();
    date.setHours(12,0,0,0);
    let days=Math.max(0,Number(this.dataset.processingDays||1));
    const business=this.dataset.skipWeekends==='true';
    while(days>0){date.setDate(date.getDate()+1);if(!business||![0,6].includes(date.getDay()))days--}
    target.textContent=this.formatDeliveryDate(date);
    this.scheduleDeliveryRefresh();
  }
}
if(!customElements.get('ritual-classic-purchase'))customElements.define('ritual-classic-purchase',RitualClassicPurchase);
